import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Form,
  Header,
  Icon,
  Input,
  Label,
  Message,
  Segment,
  Statistic,
} from "semantic-ui-react";
import { useBooking } from "../../context/BookingContext";
import GuestHouseDropdown from "./GuestHouseDropdown";
import FrontDeskModal from "./FrontDeskModal";
import RoomReservationsModal from "./RoomReservationsModal";
import LastUpdatedBadge from "./LastUpdatedBadge";
import RoomTypeIcon from "./RoomTypeIcon";
import {
  fmtDate,
  fmtTime,
  HOLDING_STATUSES,
  minutesLeft,
  STATUS_COLOR,
  todayStr,
} from "./adminHelpers";

const naturalSort = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true });

/* Local-time arithmetic — new Date(dateStr) parses a plain YYYY-MM-DD as UTC
   midnight, so adding a day and re-serializing can land on the wrong local
   calendar day near a timezone boundary. */
const addDays = (dateStr, delta) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

/* Which of a room's holders the single tile speaks for. A room can be held by
   two bookings on one date — a same-day turnover, where the morning's guest
   checks out and the evening's arrival is already confirmed into the same
   room — and the guest physically in the room is the one the tile has to
   show: calling an occupied room "Reserved" hides its occupant from the desk.
   Between two of a kind the earlier arrival leads, so the order is stable
   rather than whatever the API happened to send. That was the old behaviour —
   the last holder in the list won, and the list arrives ordered by
   -created_at, so a reservation booked weeks earlier could paint an occupied
   room yellow. */
const byOccupancyThenArrival = (x, y) => {
  const xIn = Boolean(x.assignment.actualCheckin);
  const yIn = Boolean(y.assignment.actualCheckin);
  if (xIn !== yIn) return xIn ? -1 : 1;
  /* The room's own start, not the booking's: two stays of one booking on one
     room would otherwise tie forever and order arbitrarily. */
  const byStart = String(x.effectiveStart).localeCompare(String(y.effectiveStart));
  return byStart || x.assignment.id - y.assignment.id;
};

/* room id -> every assignment holding it on `mapDate` (not checked-out, not
   no-show), each with the booking it belongs to, ordered by the rule above so
   [0] is the holder the tile represents and the rest are that date's other
   holders. Held window is the assignment's own expected window (each room on
   a booking can be staggered), falling back to the booking's at either end —
   the same rule the backend's availability checks use (see
   bookings/occupancy.py `scheduled_window`) — so a room doesn't stay
   "Occupied" on the map forever just because actualCheckout hasn't been
   recorded yet, and a room held for three days of an eight-day indent reads
   Vacant on the other five. The one exception is
   *today*: a guest who is checked in and past their expected checkout is
   still physically in the room, so today's view keeps showing it Occupied
   (flagged `overdue`) rather than risking a double-booking by calling it
   Vacant. */
export function holdersByRoom({ bookings, roomIds, mapDate, today }) {
  const map = new Map();
  for (const b of bookings) {
    for (const a of b.roomAssignments || []) {
      if (!roomIds.has(a.roomId)) continue;
      if (a.actualCheckout || a.isNoShow) continue;
      const effectiveStart = a.effectiveArrival || b.checkIn;
      const effectiveEnd = a.effectiveDeparture || b.checkOut;
      const withinWindow = effectiveStart <= mapDate && effectiveEnd >= mapDate;
      const overdue = Boolean(a.actualCheckin) && mapDate === today && effectiveEnd < mapDate;
      if (!withinWindow && !overdue) continue;
      const held = map.get(a.roomId) || [];
      held.push({ assignment: a, booking: b, overdue, effectiveStart, effectiveEnd });
      map.set(a.roomId, held);
    }
  }
  for (const held of map.values()) held.sort(byOccupancyThenArrival);
  return map;
}

/* Occupied > Maintenance > Reserved > Vacant. A guest physically in the room
   outranks a cleaning hold — an occupied tile must never read Maintenance —
   but a mere reservation does not. A room left dirty at 8 PM with an arrival
   due at 7 PM the next evening is exactly what housekeeping needs to see, and
   letting the reservation pick the colour painted it yellow and hid that. The
   reservation is still named on the tile and listed in the popup; it just no
   longer decides the colour. */
export function deriveRoomStatus(room, holder) {
  if (holder?.assignment?.actualCheckin) return "Occupied";
  if (room.status === "maintenance") return "Maintenance";
  if (holder) return "Reserved";
  return "Vacant";
}

/* The names shown under a tile: the guests physically in the room, and no one
   else — a reservation's guest is not there yet, so naming them would read as
   an occupant. Display only; it never feeds the status, counts or holders.
   The room's own guest name comes first (a multi-room indent names each
   room's occupant), falling back to the booking's. A dorm can hold more than
   one checked-in booking, so every checked-in holder is named. */
export function occupantNames({ holder, alsoHeldBy = [] }) {
  const names = [holder, ...alsoHeldBy]
    .filter((h) => h?.assignment?.actualCheckin)
    .map((h) => (h.assignment.guestName || h.booking.guestName || "").trim())
    .filter(Boolean);
  return [...new Set(names)];
}

/* What the Room Map search compares: letters and digits only, lower-cased —
   so "dr.", "Dr" and "DR" are the same, "dr rohit" finds "DR. ROHIT" and
   "Dr.Rohit", and "kr" finds "K.R. RAO". `at[i]` is where key[i] came from in
   the original text, so a hit can be marked on the name as stored. Combining
   marks are kept so Devanagari names still match. */
const KEPT = /[\p{L}\p{M}\p{N}]/u;
export function searchKey(text) {
  let key = "";
  const at = [];
  const str = String(text || "");
  for (let i = 0; i < str.length; i++) {
    if (!KEPT.test(str[i])) continue;
    const lower = str[i].toLowerCase();
    key += lower;
    for (let k = 0; k < lower.length; k++) at.push(i);
  }
  return { key, at };
}

/* Whether a tile's occupants match the Room Map search, by searchKey. An
   empty query (or one of only dots and spaces) matches nothing. */
export function matchesGuestSearch(room, query) {
  const q = searchKey(query).key;
  if (!q) return false;
  return (room.occupants || []).some((n) => searchKey(n).key.includes(q));
}

/* A name cut into runs around every hit of the query (by searchKey), so the
   tile can mark the letters that matched. A hit spans from its first to its
   last matched character, taking any dots or spaces in between with it. */
export function splitOnMatch(name, query) {
  const q = searchKey(query).key;
  if (!q) return [{ text: name, hit: false }];
  const { key, at } = searchKey(name);
  const parts = [];
  let done = 0; // chars of `name` already emitted
  let from = 0; // search position in `key`
  for (let i = key.indexOf(q, from); i !== -1; i = key.indexOf(q, from)) {
    const start = at[i];
    const end = at[i + q.length - 1] + 1;
    if (start > done) parts.push({ text: name.slice(done, start), hit: false });
    parts.push({ text: name.slice(start, end), hit: true });
    done = end;
    from = i + q.length;
  }
  if (done < name.length) parts.push({ text: name.slice(done), hit: false });
  return parts;
}

export default function RoomMapTab({
  guestHouses,
  onOpenBill,
  onOpenDetail,
  ghId: ghIdParam,
  mapDate: mapDateParam,
  onViewChange,
  initialRoomId,
  onInitialSelectionConsumed,
}) {
  const {
    rooms: allRooms,
    bookings: allBookings,
    roomTypes,
    apiError,
    apiLoading,
    lastSyncedAt,
    reloadFromApi,
    updateBookingLocal,
  } = useBooking();
  /* There's no "All guest houses" view here, unlike the shared `gh` filter
     elsewhere — an absent/"All" param resolves to the first guest house. */
  const ghId = ghIdParam && ghIdParam !== "All" ? Number(ghIdParam) : guestHouses[0]?.id ?? null;
  const mapDate = mapDateParam || todayStr();
  const [refreshing, setRefreshing] = useState(false);
  const [activeBookingId, setActiveBookingId] = useState(null);
  /* By id, so the open popup follows the room's live status after a change. */
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  /* Guest search — highlights tiles only; never filters what the map counts. */
  const [search, setSearch] = useState("");
  /* How many times Enter has jumped since the query last changed (-1: not
     yet). Typing only highlights; the page moves on Enter alone. Scrolling
     while typing pushed the search box off screen, and the browser then
     scrolled the focused box back into view on the next keystroke — the map
     snapped back up after every jump. Enter types nothing, so it doesn't. */
  const [matchStep, setMatchStep] = useState(-1);
  const tileRefs = useRef(new Map());

  /* Write the resolved default guest house into the URL once it's known, so
     the address bar (and a copied link) reflects what's actually showing. */
  useEffect(() => {
    if ((!ghIdParam || ghIdParam === "All") && guestHouses.length) {
      onViewChange({ gh: guestHouses[0].id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestHouses, ghIdParam]);

  const stepMapDate = (delta) => {
    const v = addDays(mapDate, delta);
    onViewChange({ date: v < todayStr() ? todayStr() : v });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await reloadFromApi();
    } finally {
      setRefreshing(false);
    }
  };

  const rooms = useMemo(
    () => allRooms.filter((r) => Number(r.guestHouseId) === Number(ghId)),
    [allRooms, ghId],
  );
  const roomIds = useMemo(() => new Set(rooms.map((r) => r.id)), [rooms]);
  /* Deliberately *not* filtered by booking.guestHouseId: that's the house the
     indent was filed with (assigned_gh), which is not always the house whose
     room the guest actually holds — a sub-request or a cross-house allotment
     parks the guest in another property's room while the booking stays with
     the original one. Filtering on it hid those holders and left a genuinely
     reserved room showing Vacant here (while the room's own popup, which
     reads the unfiltered list, still listed the reservation). The room ids
     below are what scopes this to the selected house. */
  const bookings = useMemo(
    () => allBookings.filter((b) => HOLDING_STATUSES.includes(b._apiStatus)),
    [allBookings],
  );
  /* attrs come straight from context's per-GH room-type list — same objects
     BookingContext already loads via roomTypeApi.list(), no separate fetch. */
  const iconAttrsByTypeId = useMemo(
    () => Object.fromEntries((roomTypes[ghId] || []).map((t) => [t.id, t.attributes || {}])),
    [roomTypes, ghId],
  );

  const holdersByRoomId = useMemo(
    () => holdersByRoom({ bookings, roomIds, mapDate, today: todayStr() }),
    [bookings, roomIds, mapDate],
  );

  const roomsWithStatus = useMemo(
    () =>
      rooms.map((r) => {
        const [holder, ...alsoHeldBy] = holdersByRoomId.get(r.id) || [];
        return {
          ...r,
          roomStatus: deriveRoomStatus(r, holder),
          holder,
          alsoHeldBy,
          occupants: occupantNames({ holder, alsoHeldBy }),
          overdue: holder?.overdue || false,
          iconAttrs: iconAttrsByTypeId[r.roomTypeId],
        };
      }),
    [rooms, holdersByRoomId, iconAttrsByTypeId],
  );

  const selectedRoom = roomsWithStatus.find((r) => r.id === selectedRoomId) || null;

  /* Re-open the popup for a room we navigated away from (into the full
     booking-details page) once ghId/mapDate have caught up and this room's
     status is available — see the `room` URL param in useAdminUrlState. */
  useEffect(() => {
    if (!initialRoomId) return;
    const room = roomsWithStatus.find((r) => String(r.id) === String(initialRoomId));
    if (room) setSelectedRoomId(room.id);
    onInitialSelectionConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomsWithStatus]);

  const floors = useMemo(() => {
    const byFloor = new Map();
    for (const r of roomsWithStatus) {
      const key = r.floor || "Unassigned";
      if (!byFloor.has(key)) byFloor.set(key, []);
      byFloor.get(key).push(r);
    }
    return [...byFloor.entries()]
      .sort(([a], [b]) => {
        if (a === "Unassigned") return 1;
        if (b === "Unassigned") return -1;
        /* Letter-led floor labels (e.g. "GF") sort before purely numeric
           ones (e.g. "1", "2") — natural sort alone doesn't guarantee this. */
        const aNumeric = /^\d/.test(a);
        const bNumeric = /^\d/.test(b);
        if (aNumeric !== bNumeric) return aNumeric ? 1 : -1;
        return naturalSort(a, b);
      })
      .map(([floor, list]) => [
        floor,
        [...list].sort((x, y) => naturalSort(x.roomNumber, y.roomNumber)),
      ]);
  }, [roomsWithStatus]);

  const searching = search.trim() !== "";
  /* In display order, so [0] is the first match on screen. */
  const matchedIds = useMemo(
    () =>
      floors.flatMap(([, list]) => list.filter((r) => matchesGuestSearch(r, search)).map((r) => r.id)),
    [floors, search],
  );
  const matchedSet = useMemo(() => new Set(matchedIds), [matchedIds]);
  /* The match Enter last jumped to — drawn stronger than the other matches. */
  const focusedMatchId =
    matchStep >= 0 && matchedIds.length ? matchedIds[matchStep % matchedIds.length] : null;

  const changeSearch = (value) => {
    setSearch(value);
    setMatchStep(-1);
  };

  /* In the handler, not an effect, so a background refresh that reorders or
     changes the matches can never move the page on its own. */
  const jumpToNextMatch = () => {
    if (!matchedIds.length) return;
    const next = matchStep + 1;
    setMatchStep(next);
    tileRefs.current
      .get(matchedIds[next % matchedIds.length])
      ?.scrollIntoView?.({ block: "center", behavior: "smooth" });
  };

  const counts = useMemo(() => {
    const c = { Vacant: 0, Reserved: 0, Occupied: 0, Maintenance: 0 };
    roomsWithStatus.forEach((r) => {
      c[r.roomStatus] += 1;
    });
    return c;
  }, [roomsWithStatus]);

  const handleUpdated = (mapped) => {
    updateBookingLocal(mapped);
  };

  const activeBooking = bookings.find((b) => b.id === activeBookingId) || null;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {guestHouses.length > 1 && (
            <GuestHouseDropdown
              value={ghId}
              options={guestHouses.map((g) => ({ value: g.id, label: g.name }))}
              onChange={(v) => onViewChange({ gh: Number(v) })}
            />
          )}
          <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
            <Button
              type="button"
              icon
              basic
              disabled={mapDate <= todayStr()}
              onClick={() => stepMapDate(-1)}
              title="Previous day"
            >
              <Icon name="angle left" />
            </Button>
            <Form.Input
              type="date"
              label="Date"
              value={mapDate}
              min={todayStr()}
              onChange={(e) => {
                const v = e.target.value;
                onViewChange({ date: v < todayStr() ? todayStr() : v });
              }}
            />
            <Button type="button" icon basic onClick={() => stepMapDate(1)} title="Next day">
              <Icon name="angle right" />
            </Button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Input
              aria-label="Find guest"
              icon="search"
              iconPosition="left"
              placeholder="Find a checked-in guest by name"
              value={search}
              onChange={(e) => changeSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") changeSearch("");
                if (e.key === "Enter") jumpToNextMatch();
              }}
              action={
                searching
                  ? { icon: "close", basic: true, type: "button", title: "Clear search", onClick: () => changeSearch("") }
                  : undefined
              }
              style={{ width: 280, maxWidth: "100%" }}
            />
            <span aria-live="polite">
              {searching &&
                (matchedIds.length ? (
                  <Label basic color="blue">
                    {matchStep < 0
                      ? `${matchedIds.length} match${matchedIds.length === 1 ? "" : "es"}`
                      : `${(matchStep % matchedIds.length) + 1} of ${matchedIds.length}`}
                    {(matchStep < 0 || matchedIds.length > 1) && (
                      <Label.Detail>{matchStep < 0 ? "Enter to jump" : "Enter for next"}</Label.Detail>
                    )}
                  </Label>
                ) : (
                  <Label basic color="red">No checked-in guest matches</Label>
                ))}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <LastUpdatedBadge lastSyncedAt={lastSyncedAt} syncing={apiLoading} />
          <Button type="button" icon onClick={handleRefresh} loading={refreshing} disabled={refreshing} title="Refresh">
            <Icon name="refresh" />
          </Button>
        </div>
      </div>

      {apiError && (
        <Message negative>
          <Icon name="exclamation triangle" />
          {apiError}
          <div style={{ marginTop: 12 }}>
            <Button type="button" basic onClick={handleRefresh}>
              <Icon name="refresh" /> Retry
            </Button>
          </div>
        </Message>
      )}
      <Segment>
        <Statistic.Group size="small" widths={5}>
          <Statistic>
            <Statistic.Value>{roomsWithStatus.length}</Statistic.Value>
            <Statistic.Label>Total</Statistic.Label>
          </Statistic>
          <Statistic color={STATUS_COLOR.Vacant}>
            <Statistic.Value>{counts.Vacant}</Statistic.Value>
            <Statistic.Label>Vacant</Statistic.Label>
          </Statistic>
          <Statistic color={STATUS_COLOR.Reserved}>
            <Statistic.Value>{counts.Reserved}</Statistic.Value>
            <Statistic.Label>Reserved</Statistic.Label>
          </Statistic>
          <Statistic color={STATUS_COLOR.Occupied}>
            <Statistic.Value>{counts.Occupied}</Statistic.Value>
            <Statistic.Label>Occupied</Statistic.Label>
          </Statistic>
          <Statistic color={STATUS_COLOR.Maintenance}>
            <Statistic.Value>{counts.Maintenance}</Statistic.Value>
            <Statistic.Label>Maintenance</Statistic.Label>
          </Statistic>
        </Statistic.Group>
        <div style={{ marginTop: 14, display: "flex", gap: 16, flexWrap: "wrap" }}>
          {Object.entries(STATUS_COLOR).map(([label, color]) => (
            <Label key={label} basic color={color}>
              <Icon name="circle" />
              {label}
            </Label>
          ))}
        </div>
      </Segment>

      {roomsWithStatus.length === 0 ? (
        <Message>No rooms found for this guest house.</Message>
      ) : (
        floors.map(([floor, list]) => (
          <Segment key={floor}>
            <Header as="h5" content={floor === "Unassigned" ? "Unassigned Floor" : `Floor ${floor}`} />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
              {list.map((r) => {
                /* Only an occupant can hide the room's own condition now —
                   maintenance outranks a reservation, so a dirty reserved room
                   already paints red. A guest sleeping in a room flagged for
                   repair still gets an Occupied tile, and this is what says so. */
                const heldButInMaintenance = r.status === "maintenance" && r.roomStatus === "Occupied";
                /* A Maintenance tile's colour no longer names any of its
                   holders, so every one of them has to be listed; other tiles
                   show holder[0] and only need the rest named — on a turnover
                   day the second booking would otherwise be invisible until
                   someone opens the popup. */
                const unnamedHolders =
                  r.roomStatus === "Maintenance"
                    ? [r.holder, ...r.alsoHeldBy].filter(Boolean)
                    : r.alsoHeldBy;
                /* The room's own start, not the booking's — an extended or
                   moved stay holds the room on different dates. */
                const describeHolder = (h) => {
                  const time = h.assignment.effectiveArrivalTime || h.booking.arrivalTime;
                  return `${h.booking.bookingCode} from ${fmtDate(h.effectiveStart)}${
                    time ? ` ${fmtTime(time)}` : ""
                  }`;
                };
                const alsoHeld = unnamedHolders.length
                  ? ` — ${r.roomStatus === "Maintenance" ? "reserved" : "also held"}: ${unnamedHolders
                      .map(describeHolder)
                      .join(", ")}`
                  : "";
                /* Every tile on screen belongs to the one guest house picked
                   above, unlike a mixed list (BookingsTab, RoomNumbersCell)
                   where the short-code prefix disambiguates rooms across
                   houses — here it would just be redundant noise. */
                const displayRoomNumber = r.roomNumber;
                const matched = matchedSet.has(r.id);
                const focused = r.id === focusedMatchId;
                return (
                  <div
                    key={r.id}
                    ref={(el) => {
                      if (el) tileRefs.current.set(r.id, el);
                      else tileRefs.current.delete(r.id);
                    }}
                    data-search-match={searching ? String(matched) : undefined}
                    data-focused-match={focused || undefined}
                    onClick={() => setSelectedRoomId(r.id)}
                    style={{
                      width: 136,
                      /* The bed-count badge sits ~12px above the icon's
                         circle, so the top needs room to keep it inside. */
                      padding: "18px 8px 8px",
                      textAlign: "center",
                      cursor: "pointer",
                      borderRadius: 10,
                      border: "2px solid",
                      /* Every tile keeps the same padding and border so a
                         highlight never shifts the grid. */
                      borderColor: matched ? "#2185d0" : "transparent",
                      background: matched ? "rgba(33, 133, 208, 0.08)" : "transparent",
                      /* A ring outside the border, so the focused tile stands
                         out without changing its size. */
                      boxShadow: focused ? "0 0 0 3px rgba(33, 133, 208, 0.35)" : "none",
                      transition: "opacity 0.15s, background 0.15s",
                      ...(searching && !matched ? { opacity: 0.35 } : {}),
                    }}
                    title={
                      r.roomStatus === "Maintenance"
                        ? `Room ${displayRoomNumber} — ${r.roomType || "—"} (${r.roomStatus}): ${
                            r.maintenanceRemark || "No remark"
                          }${
                            minutesLeft(r.maintenanceUntil) ? ` — ~${minutesLeft(r.maintenanceUntil)} min left` : ""
                          }${alsoHeld}`
                        : r.overdue
                          ? `Room ${displayRoomNumber} — ${r.roomType || "—"} (${r.roomStatus}) — checkout overdue since ${fmtDate(r.holder.effectiveEnd)}${alsoHeld}`
                          : `Room ${displayRoomNumber} — ${r.roomType || "—"} (${r.roomStatus})${
                              heldButInMaintenance ? ` — under maintenance: ${r.maintenanceRemark || "No remark"}` : ""
                            }${alsoHeld}`
                    }
                  >
                    <RoomTypeIcon
                      capacity={r.capacity}
                      attrs={r.iconAttrs}
                      color={STATUS_COLOR[r.roomStatus]}
                      overdue={r.overdue}
                    />
                    <div style={{ fontWeight: "bold", marginTop: 10 }}>
                      {displayRoomNumber}
                      {heldButInMaintenance && <Icon name="wrench" color="grey" style={{ marginLeft: 4 }} />}
                      {/* A red tile that someone is due to arrive into reads
                          the same as one simply out of service — mark the
                          difference, since the room has to be cleaned before
                          that arrival. */}
                      {r.roomStatus === "Maintenance" && r.holder && (
                        <Icon name="calendar alternate outline" color="yellow" style={{ marginLeft: 4 }} />
                      )}
                    </div>
                    {r.occupants.length > 0 && (
                      <div
                        className="room-map-guest"
                        title={r.occupants.join(", ")}
                        style={{
                          fontSize: "0.8em",
                          lineHeight: 1.3,
                          color: focused ? "#fff" : matched ? "rgba(0,0,0,.87)" : "rgba(0,0,0,.6)",
                          /* The focused match's name reads white on blue. */
                          background: focused ? "#2185d0" : "transparent",
                          fontWeight: focused ? 600 : "normal",
                          borderRadius: 4,
                          padding: "1px 4px",
                          marginTop: 4,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          wordBreak: "break-word",
                        }}
                      >
                        {splitOnMatch(r.occupants[0], matched ? search : "").map((part, i) =>
                          part.hit ? (
                            <mark
                              key={i}
                              style={{
                                /* Yellow is lost on the focused name's blue — underline there. */
                                background: focused ? "transparent" : "#fff1a8",
                                textDecoration: focused ? "underline" : "none",
                                textUnderlineOffset: 2,
                                color: "inherit",
                                padding: 0,
                                borderRadius: 2,
                              }}
                            >
                              {part.text}
                            </mark>
                          ) : (
                            <span key={i}>{part.text}</span>
                          ),
                        )}
                        {r.occupants.length > 1 && (
                          <span style={{ color: focused ? "rgba(255,255,255,.8)" : "rgba(0,0,0,.45)" }}>
                            {` +${r.occupants.length - 1}`}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Segment>
        ))
      )}

      {activeBooking && (
        <FrontDeskModal
          booking={activeBooking}
          guestHouses={guestHouses}
          onClose={() => setActiveBookingId(null)}
          onUpdated={handleUpdated}
          onOpenBill={onOpenBill}
        />
      )}

      {selectedRoom && (
        <RoomReservationsModal
          room={selectedRoom}
          bookings={allBookings}
          onClose={() => setSelectedRoomId(null)}
          onOpenFrontDesk={setActiveBookingId}
          onViewBooking={(bookingId) => {
            const booking = allBookings.find((b) => b.id === bookingId);
            if (booking) {
              onOpenDetail(booking, { roomId: selectedRoom.id, ghId, mapDate });
            }
          }}
        />
      )}
    </div>
  );
}
